
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { BarChart, Users, Search, UserCog, Shield, LogOut } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

interface DashboardHeaderProps {
  userName: string;
}

const DashboardHeader = ({ userName }: DashboardHeaderProps) => {
  const { user, logout, isAdmin, hasPermission } = useAuth();

  return (
    <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-8">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-gray-800 to-gray-600">
          Bem-vindo, {userName}
        </h1>
        <p className="text-gray-600 mt-1">Aqui está uma visão geral dos serviços da plataforma</p>
      </div>
      <div className="mt-4 md:mt-0 flex gap-2">
        <div className="relative md:w-64">
          <input
            type="text"
            placeholder="Buscar..."
            className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-gray-200 focus:border-transparent"
          />
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
        </div>

        {hasPermission('canViewReports') && (
          <Button asChild variant="outline" className="rounded-full border-gray-200 text-gray-700 hover:bg-gray-50 hover:text-gray-900">
            <Link to="/relatorios">
              <BarChart className="h-4 w-4 mr-2" />
              Relatórios
            </Link>
          </Button>
        )}

        {(isAdmin() || hasPermission('canManageClients')) && (
          <Button asChild className="rounded-full bg-energy-green-dark hover:bg-energy-green text-white">
            <Link to="/clientes/novo">
              <Users className="h-4 w-4 mr-2" />
              Novo Cliente
            </Link>
          </Button>
        )}

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="relative rounded-full h-10 w-10 p-0 overflow-hidden border border-gray-200">
              <Avatar className="h-10 w-10">
                <AvatarImage src={user?.profilePicture || ""} alt={userName} />
                <AvatarFallback className="bg-energy-green text-white">
                  {userName.substring(0, 2).toUpperCase()}
                </AvatarFallback>
              </Avatar>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-56 mt-1 z-50" align="end">
            <DropdownMenuLabel>Minha Conta</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild>
              <Link to="/users/profile" className="flex items-center cursor-pointer">
                <UserCog className="w-4 h-4 mr-2" />
                Perfil e Configurações
              </Link>
            </DropdownMenuItem>
            
            {isAdmin() && (
              <DropdownMenuItem asChild>
                <Link to="/users" className="flex items-center cursor-pointer">
                  <Shield className="w-4 h-4 mr-2" />
                  Gerenciar Permissões
                </Link>
              </DropdownMenuItem>
            )}
            
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={logout} className="text-red-600 cursor-pointer">
              <LogOut className="w-4 h-4 mr-2" />
              Sair
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
};

export default DashboardHeader;
